import React, { useEffect, useState } from 'react';
import {
    Grid,
    Paper,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    FormControl,
    Typography
} from '@mui/material';
import moment from 'moment';
import { toast } from 'react-toastify';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MainCard from 'components/Cards/MainCard';
import SubCard from 'components/Cards/SubCard';
import ReportFileExports from 'components/Reports/FileExports';
import ApiHelper from 'utils/apiHelper';
import { gridSpacing, dateRangeOptions } from 'utils/constants';
import { handleUpdateReportDateRange, convertDate } from 'utils/commonHelpers';

const initialState = {
    summary_title: '',
    table_headers: [],
    rows_data: []
};

const ReportsSavingsSummary = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const currentDate = new Date();
    const today = new Date();
    const [startDate, setStartDate] = useState(currentDate.setMonth(currentDate.getMonth() - 1));
    const [endDate, setEndDate] = useState(today.setDate(today.getDate() - 1));
    const [dateRangeValue, setDateRangeValue] = useState('1month');
    const [groupId, setGroupId] = useState(0);
    const [policyId, setPolicyId] = useState(0);
    const [deviceTypeId, setDeviceTypeId] = useState(0);
    const [groupsData, setGroupsData] = useState([]);
    const [policiesData, setPoliciesData] = useState([]);
    const [deviceTypesData, setDeviceTypesData] = useState([]);
    const [savingsSummaryData, setSavingsSummaryData] = useState(initialState);
    const [filtersData, setFiltersData] = useState([]);

    useEffect(() => {
        getGroups();
        getPolicies();
        getDeviceTypes();
    }, []);

    const getGroups = () => {
        ApiHelper.get('/api/reports/groups/')
            .then((res) => {
                setGroupsData(res.data);
            })
            .catch();
    };

    const getPolicies = () => {
        ApiHelper.get('/api/management/policy/')
            .then((res) => {
                setPoliciesData(res.data);
            })
            .catch();
    };

    const getDeviceTypes = () => {
        ApiHelper.get('/api/reports/device_types/')
            .then((res) => {
                setDeviceTypesData(res.data);
            })
            .catch();
    };

    const handleChangeDateRangeValue = (e) => {
        const value = e.target.value;
        setDateRangeValue(value);
        const updatedStartDate = handleUpdateReportDateRange(value);
        setStartDate(updatedStartDate);
    };

    const getSavingsSummaryData = () => {
        if (convertDate(startDate) === 'Invalid date') {
            toast.error('Fail! The start date format is incorrect');
            return true;
        }
        if (convertDate(endDate) === 'Invalid date') {
            toast.error('Fail! The end date format is incorrect');
            return true;
        }
        if (moment(convertDate(startDate)) > moment(convertDate(endDate))) {
            toast.error('Fail! The start date can not be greater than the end date');
            return true;
        }
        setLoading(true);
        ApiHelper.post('/api/reports/savings_summary/', {
            start_date: convertDate(startDate),
            end_date: convertDate(endDate),
            device_type_id: deviceTypeId === 0 ? '' : deviceTypeId.toString(),
            group_id: groupId === 0 ? '' : groupId.toString(),
            policy_id: policyId === 0 ? '' : policyId.toString()
        })
            .then((res) => {
                setSavingsSummaryData(res.data);
                setLoading(false);
                toast.success('Successfully done');
                let groupName = 'Any';
                let policyName = 'Any';
                let deviceTypeName = 'Any';
                for (let i = 0; i < groupsData.length; i++) {
                    if (groupsData[i].id === groupId) {
                        groupName = groupsData[i].name;
                    }
                }
                for (let i = 0; i < policiesData.length; i++) {
                    if (policiesData[i].id === policyId) {
                        policyName = policiesData[i].name;
                    }
                }
                for (let i = 0; i < deviceTypesData.length; i++) {
                    if (deviceTypesData[i].id === deviceTypeId) {
                        deviceTypeName = deviceTypesData[i].name;
                    }
                }
                const temp = [
                    { label: 'Start Date', value: convertDate(startDate) },
                    { label: 'End Date', value: convertDate(endDate) },
                    { label: 'Group Name', value: groupName },
                    { label: 'Policy Name', value: policyName },
                    { label: 'Device Type', value: deviceTypeName }
                ];
                setFiltersData(temp);
            })
            .catch(() => {
                setSavingsSummaryData(initialState);
                setLoading(false);
                toast.warning('There is no savings summary data');
            });
    };

    return (
        <MainCard title="Activity Reports Management" cardHeaderTitle="Savings Summary" cardHeaderShow={true}>
            <Grid container spacing={gridSpacing}>
                <Grid item xs={10}>
                    <Typography sx={{ fontSize: '16px' }}>Provides a annualized summary of savings rates for the organization</Typography>
                </Grid>
                {savingsSummaryData.table_headers.length > 0 && (
                    <Grid item xs={2}>
                        <ReportFileExports
                            elementId="savingsSummary"
                            reportTitle="Savings Summary"
                            downloadFileName="savings_summary"
                            downloadFileOption="all"
                            data={savingsSummaryData.rows_data}
                            headers={savingsSummaryData.table_headers}
                            filtersData={filtersData}
                        />
                    </Grid>
                )}
                <Grid item xs={3}>
                    <SubCard title="Filters">
                        <Grid container>
                            <Grid item xs={12}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Start Date"
                                        value={startDate}
                                        maxDate={endDate}
                                        onChange={(newValue) => {
                                            setStartDate(newValue);
                                        }}
                                        renderInput={(params) => <TextField {...params} sx={{ width: '100%' }} />}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 2 }}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="End Date"
                                        value={endDate}
                                        maxDate={today}
                                        onChange={(newValue) => {
                                            setEndDate(newValue);
                                        }}
                                        renderInput={(params) => <TextField {...params} sx={{ width: '100%' }} />}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 2 }}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Date Range</InputLabel>
                                    <Select
                                        label="Date Range"
                                        value={dateRangeValue}
                                        name="date_range"
                                        onChange={handleChangeDateRangeValue}
                                    >
                                        {dateRangeOptions.map((data, key) => (
                                            <MenuItem value={data.value} key={key}>
                                                {data.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 2 }}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select Device Type</InputLabel>
                                    <Select
                                        label="Select Device Type"
                                        value={deviceTypeId}
                                        name="device_type"
                                        onChange={(e) => setDeviceTypeId(Number(e.target.value))}
                                    >
                                        <MenuItem value={0}>Any</MenuItem>
                                        {deviceTypesData.length > 0 &&
                                            deviceTypesData.map((data, key) => (
                                                <MenuItem value={data.id} key={key}>
                                                    {data.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 2 }}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select Group</InputLabel>
                                    <Select
                                        label="Select Group"
                                        value={groupId}
                                        name="group"
                                        onChange={(e) => setGroupId(Number(e.target.value))}
                                    >
                                        <MenuItem value={0}>Any</MenuItem>
                                        {groupsData.length > 0 &&
                                            groupsData.map((data, key) => (
                                                <MenuItem value={data.id} key={key}>
                                                    {data.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 2 }}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select Policy</InputLabel>
                                    <Select
                                        label="Select Policy"
                                        value={policyId}
                                        name="policy"
                                        onChange={(e) => setPolicyId(Number(e.target.value))}
                                    >
                                        <MenuItem value={0}>Any</MenuItem>
                                        {policiesData.length > 0 &&
                                            policiesData.map((data, key) => (
                                                <MenuItem value={data.policyid} key={key}>
                                                    {data.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 4 }}>
                                <LoadingButton
                                    color="primary"
                                    variant="contained"
                                    sx={{ width: '100%' }}
                                    loading={loading}
                                    onClick={getSavingsSummaryData}
                                >
                                    Submit
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </SubCard>
                </Grid>
                <Grid item xs={9}>
                    <Paper sx={{ overflowX: 'hidden' }} id="savingsSummary">
                        {loading === true ? (
                            <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '16px', fontWeight: 'bold' }}>
                                Please wait...
                            </Typography>
                        ) : savingsSummaryData.table_headers.length === 0 ? (
                            <Typography sx={{ textAlign: 'center', mt: 3 }} color={theme.palette.secondary.main}>
                                No data for the savings summary report
                            </Typography>
                        ) : (
                            <Table sx={{ width: '50%' }} id="savingsSummary">
                                <TableHead>
                                    <TableRow>
                                        {savingsSummaryData.table_headers.map((data, key) => (
                                            <TableCell key={key} sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                                                {data}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {savingsSummaryData.rows_data.map((data, key) => (
                                        <TableRow key={key}>
                                            <TableCell sx={{ fontWeight: 'bold' }}>{data[0]}</TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>{data[1]}</TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>{data[2]}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </MainCard>
    );
};

export default ReportsSavingsSummary;
